import { alpha } from '@mui/material/styles';

import { COLOR_PALETTE } from 'constants/ui';

/** Opacity applied to the disabled "Start with photos" method card. */
const DISABLED_CARD_OPACITY = 0.7;

/** Primary-tint opacity for the highlighted card's dark-mode fill. */
const DARK_HIGHLIGHT_TINT_ALPHA = 0.16;

/**
 * Highlight fill for the selected "Start with photos" method card: the light
 * blue tint in light mode (matches the mockup), and a primary-tinted DARK fill
 * in dark mode so the card text (text.primary / text.secondary, which flip to
 * light) stays legible. The prior hardcoded COLOR_PALETTE.BLUE[1] never flipped,
 * leaving white-on-near-white text in dark mode.
 */
const getMethodHighlightBackground = palette =>
  palette.mode === 'dark'
    ? alpha(palette.primary.main, DARK_HIGHLIGHT_TINT_ALPHA)
    : COLOR_PALETTE.BLUE[1];

export const drawerPaperStyles = {
  borderRadius: '28px 28px 0 0',
  backgroundColor: ({ palette }) => palette.background.paper,
  backdropFilter: 'saturate(180%) blur(22px)',
  WebkitBackdropFilter: 'saturate(180%) blur(22px)',
  borderTop: '1px solid',
  borderColor: ({ palette }) => palette.divider,
  paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 20px)',
  maxHeight: '88dvh',
  overflow: 'auto',
};

export const grabberStyles = {
  width: '38px',
  height: '6px',
  borderRadius: '999px',
  backgroundColor: ({ palette }) => palette.action.disabled,
  margin: '8px auto 12px',
};

export const sheetHeaderStyles = {
  padding: '0 16px 8px',
};

export const forkHeaderStyles = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  padding: '0 16px 8px',
};

export const backButtonStyles = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '32px',
  height: '32px',
  border: 'none',
  background: 'transparent',
  cursor: 'pointer',
  color: 'text.secondary',
  WebkitTapHighlightColor: 'transparent',
};

export const pickerListStyles = {
  padding: '8px 16px 4px',
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
};

export const optionButtonStyles = {
  display: 'flex',
  alignItems: 'center',
  gap: '12px',
  width: '100%',
  minHeight: '64px',
  padding: '12px',
  borderRadius: '14px',
  border: '1px solid',
  borderColor: ({ palette }) => palette.divider,
  background: 'transparent',
  cursor: 'pointer',
  textAlign: 'left',
  WebkitTapHighlightColor: 'transparent',
  '&:active': {
    borderColor: ({ palette }) => palette.primary.main,
    backgroundColor: ({ palette }) => palette.action.hover,
  },
};

const getIconTileStyles = ({ size, radius, color }) => ({
  width: `${size}px`,
  height: `${size}px`,
  borderRadius: `${radius}px`,
  backgroundColor: color ?? (({ palette }) => palette.action.hover),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  color: ({ palette }) => palette.common.white,
});

export const getOptionIconWrapperStyles = color =>
  getIconTileStyles({ size: 40, radius: 12, color });

export const optionTextStyles = {
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  minWidth: 0,
};

export const optionChevronStyles = {
  marginLeft: 'auto',
  flexShrink: 0,
  color: 'text.secondary',
};

export const forkPromptStyles = {
  display: 'block',
  padding: '4px 16px 0',
  textTransform: 'uppercase',
};

export const methodListStyles = {
  padding: '8px 16px 4px',
  display: 'flex',
  flexDirection: 'column',
  gap: '12px',
};

export const getMethodCardStyles = ({ isDisabled, isHighlighted }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  width: '100%',
  padding: '16px',
  borderRadius: '14px',
  border: '1px solid',
  borderColor: ({ palette }) => (isHighlighted ? palette.primary.main : palette.divider),
  background: ({ palette }) =>
    isHighlighted ? getMethodHighlightBackground(palette) : 'transparent',
  textAlign: 'left',
  opacity: isDisabled ? DISABLED_CARD_OPACITY : 1,
  cursor: isDisabled ? 'default' : 'pointer',
  WebkitTapHighlightColor: 'transparent',
  '&:active': {
    backgroundColor: ({ palette }) => (isDisabled ? 'transparent' : palette.action.hover),
  },
});

export const methodTopRowStyles = {
  display: 'flex',
  alignItems: 'center',
  gap: '14px',
};

export const getMethodIconWrapperStyles = color =>
  getIconTileStyles({ size: 44, radius: 12, color });

export const methodTitleRowStyles = {
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
};

export const methodChipsRowStyles = {
  display: 'flex',
  flexWrap: 'wrap',
  gap: '6px',
};

export const comingSoonStyles = {
  flexShrink: 0,
};

export const continueWithPhotosStyles = {
  width: '100%',
  minHeight: '48px',
  borderRadius: '12px',
  textTransform: 'none',
};
